<template>
  <div class="main-content">
    <div style="width: 100%; height: 100%">
      <div
        id="map"
        ref="map"
        class="map"
        @contextmenu.prevent=""
        :type="isDarkMap ? 'dark' : 'light'"
      >
        <div style="position: absolute; width: 100%; z-index: 100">
          <v-progress-linear
            v-if="loading"
            :indeterminate="true"
            color="accent"
            background-color="white"
            background-opacity="0.7"
            height="5"
            style="margin-top: 0"
          />
        </div>
        <search-map />
        <object-list
          v-if="objectList.open"
          :coordinates="contextMenuPixel"
          :objectList="objectList.list"
          @selectObject="selectObject"
          @remove-highlight-from-menu="removeHighlightFromObjectOnLeave"
          v-on-clickaway="closeObjectList"
        />

        <object-form ref="objectForm" />
      </div>
    </div>
  </div>
</template>

<script>
import "ol/ol.css";
import { Map, View } from "ol";
import {
  BingMaps,
  ImageWMS,
  OSM,
  Vector as VectorSource,
  XYZ,
} from "ol/source";
import {
  Image as ImageLayer,
  Tile as TileLayer,
  Vector as VectorLayer,
} from "ol/layer";
import { Overlay } from "ol";
import { Point } from "ol/geom.js";
import SearchMap from "@/components/Network/Map/SearchMap";
import { mapActions } from "vuex";
import { METERS_PER_UNIT } from "ol/proj";
import { GeoJSON } from "ol/format";
import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
  RegularShape,
} from "ol/style";
import { WKT } from "ol/format";
import { bearingToAzimuth, point } from "@turf/turf";
import { get as getProjection, toLonLat, transform } from "ol/proj";
import { LineString, Polygon } from "ol/geom.js";
import { bus } from "@/main";
import { unByKey } from "ol/Observable";
import ObjectList from "@/components/Home/Content/ObjectList/ObjectList";
import { mixin as clickaway } from "vue-clickaway";
import ObjectForm from "@/components/Network/Forms/ObjectForm";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { Feature } from "ol";
import { getArea, getLength } from "ol/sphere";
import RightSidebarTools from "@/components/Network/RightSidebar/RightSidebarTools.vue";
import { Draw, Modify } from "ol/interaction.js";
import endpoints from "@/utils/endpoints";

export default {
  name: "NetworkMap",

  mixins: [clickaway, checkPermissionsMixin],

  components: {
    ObjectForm,
    ObjectList,
    SearchMap,
    RightSidebarTools,
  },

  data() {
    return {
      map: undefined,
      mapView: undefined,
      roadMapLayer: undefined,
      roadMapSource: undefined,
      openStreetMapSource: undefined,
      aerialMapLayer: undefined,
      mapboxLayer: undefined,
      objectsImageSource: undefined,
      failureAnalysisSource: undefined,
      singlePointofFailureSource: undefined,
      contextMenuPixel: [],
      contextMenuCoords: [],
      clickEvent: null,
      highlightLayerSource: undefined,
      highlightBuildingsLayerSource: undefined,
      isDarkMap: false,

      loading: false,
      checkedLayers: {},
      objectList: {
        list: [],
        open: false,
      },
      xMarkerFeature: undefined,
      measurementsDraw: null,
      measurementsLayerSource: undefined,
      measureTooltipElement: null,
      measureTooltip: null,
      singleClickEvent: null,
      sketch: null,
      pathwayVerticesCoordinates: [],
      draw: null,
      newObjectDraw: null,
      spfSelectionType: null,
    };
  },

  computed: {
    /**
     * Object that is to Zoom and Highlight on the map
     * @returns {any}
     */
    objectToZoomGeoJson() {
      return this.$store.getters.getObjectToZoomGeoJson;
    },
    //reutrn spf buildings list
    getBuildings(){
      return this.$store.getters.getSpfBuildings;
    },
    /**
     * Return map style
     * @returns {any} - "light-map" or "dark-map"
     */
    mapStyle() {
      return this.$store.getters.getCurrentMainMapStyle;
    },

    /**
     * Returns the selected tool
     */
    selectedTool() {
      return this.$store.getters.getSelectedTool;
    },

    /**
     * Indicates if the failure analysis tool is active
     */
    failureAnalysisToolIsActive() {
      return this.selectedTool && this.selectedTool.id === "failureAnalysis";
    },
    selectedMeasurementTool() {
      return this.$store.getters.getSelectedMeasurementTool;
    },
    showMeasurementsToolbar() {
      return this.$store.getters.isMeasurementsToolbarOpen;
    },
    /**
     * Indicates if the single point of failure tool is active
     */
    singlePointofFailureIsActive() {
      return (
        this.selectedTool && this.selectedTool.id === "singlePointofFailure"
      );
    },
  },

  watch: {
    /**
     * Whenever the value is changed, it zooms and highlights the desired object
     * @param objectGeoJSON
     */
    objectToZoomGeoJson(objectGeoJSON) {
      this.removeHighlightFromObjectOnLeave();
      if (objectGeoJSON) {
        let feature = new GeoJSON().readFeatures(objectGeoJSON, {
          featureProjection: "EPSG:3857",
        });
        let point = feature[0].getGeometry();
        this.mapView.fit(point, {
          padding: [150, 50, 50, 150],
          minResolution: 0.1,
        });
        this.highlightObjectOnHover(objectGeoJSON);
        this.addMarkPoint(feature[0]);
      }
    },

    /**
     * Whenever the measurement tools are not selected mesurements are cleared
     */
    selectedTool(value) {
      this.map.removeInteraction(this.measurementsDraw);
      if (!value) {
        this.clearMeasurements();
        this.enableMapClickEvents();
      } else if (
        value.id === "length" ||
        value.id === "angle" ||
        value.id === "area"
      ) {
        this.addMeasurementInteraction();
      }
      this.failureAnalysisSource.clear();
      this.singlePointofFailureSource.clear();
      this.clearAll();
    },

    /**
     * Whenever the mapStyle changes, the changeLocalMapStyle method is called.
     */
    mapStyle(value) {
      this.changeLocalMapStyle(value);
    },

    showMeasurementsToolbar(show) {
      if (!show) {
        this.clearMeasurements();
      }
    },

    currentActionType(newValue) {
      if (newValue === "" && this.selectedTool !== "") {
        this.disableMapClickEvents();
      } else {
        this.map.removeInteraction(this.newObjectDraw);
        if (newValue === "select") {
          this.resetMapClickEvents();
        }
      }
    },
  },

  mounted() {
    this.initializeMap();
    this.clearAll()

    bus.$on("update-geoserver-network-layers", () =>
      this.updateGeoserverLayers()
    );
    bus.$on("set-spf-selection-type", (type) =>
      this.spfSelectionType = type
    );
    bus.$on("remove-selected-building", () =>
      this.updateSelectedBuilding()
    );
    bus.$on("clear-all-spf", () =>
      this.clearAll()
    );
    bus.$on("cancel-spf", () =>
      this.closeSinglePointWindow()
    );
    bus.$on("cancel-fa", () =>
      this.closeFailureAnalysisWindow()
    );
    bus.$on("execute-spf", () =>
      this.runSinglePointFailure()
    );
    bus.$on("highlight-object-from-menu", (geoJSON) =>
      this.highlightObjectOnHover(geoJSON)
    );
    bus.$on("remove-highlight-from-menu", () =>
      this.removeHighlightFromObjectOnLeave()
    );

    bus.$on("open-form", (objectSelect) =>
      this.fetchObjectData(objectSelect).then((objectData) => {
        this.$refs.objectForm.openNewForm(objectSelect.layerName, objectData);
      })
    );

    bus.$on("change-map-network", (activeMap) => {
      this.changeViewType(activeMap);
    });

    this.mapClickListener();
    this.rightClickListener();
  },

  destroyed() {
    bus.$off("update-geoserver-network-layers");
    bus.$off("highlight-object-from-menu");
    bus.$off("remove-highlight-from-menu");
    bus.$off("open-form");
    bus.$off("change-map-network");
    bus.$off("change-measurement");
    bus.$off("set-spf-selection-type");
    bus.$off("remove-selected-building");
    bus.$off("clear-all-spf");
    bus.$off("cancel-spf");
    bus.$off("cancel-fa");
    bus.$off("execute-spf");
  },

  methods: {
    ...mapActions([
      "fetchImageFromGeoServer",
      "fetchFeaturesFromGeoServer",
      "fetchObjectByUUID",
      "executeFailureAnalysis",
      "setSelectedMeasureTool",
      "setMeasurementsToolbarState",
      "executeSinglePointFailure",
      "setSelectedTool",
      "addBuildingsSPF",
      "deleteAllSPF"
    ]),

    ///////////////////////////
    /* Click events' methods */
    ///////////////////////////

    /**
     * The event for clicking on the left side of the mouse is added to the map,
     * for the selection of objects on the map
     */
    mapClickListener() {
      unByKey(this.singleClickEvent);
      unByKey(this.clickEvent);
      this.clickEvent = this.map.on("click", (e) => {
        this.removeHighlightFromObjectOnLeave();
        this.getObjects(e);
      });
    },
    /**
     * The event for clicking on the right side of the mouse is added to the map,
     * for the selection of objects on the map
     */
    rightClickListener() {
      unByKey(this.rightClickEvent);
      this.rightClickEvent = this.map.on("contextmenu", (e) =>
        this.debounceRightClick(e)
      );
    },
    /**
     * executes right click in an interval of 500ms
     */
    debounceRightClick: _.debounce(function (e) {
      e.preventDefault();
      this.closeObjectList();
      this.clickType = "rightClick";
      this.getObjects(e);
    }, 500),

    /////////////////////////////
    /* Map preparation methods */
    /////////////////////////////
    initializeMap() {
      this.mapView = this.createMapView();
      this.roadMapSource = this.createRoadMapSource();
      this.openStreetMapSource = this.createOpenStreetMapSource();
      this.roadMapLayer = this.createRoadMapLayer();
      this.aerialMapLayer = this.createAerialMapLayer();
      let highlightLayer = this.createHighlightLayer();
      let highlightBuildingsLayer = this.createHighlightBuildingsLayer();
      let objectsImageLayer = this.createObjectsImageLayer();
      let failureAnalysisLayer = this.createFailureAnalysisLayer();
      let measurementsLayer = this.createMeasurementsLayer();
      let singlePointofFailureLayer = this.createSinglePointofFailureLayer();
      this.map = new Map({
        controls: [],
        layers: [
          this.roadMapLayer,
          this.aerialMapLayer,
          failureAnalysisLayer,
          singlePointofFailureLayer,
          objectsImageLayer,
          highlightLayer,
          measurementsLayer,
          highlightBuildingsLayer,
        ],
        target: "map",
        view: this.mapView,
      });
      this.getActiveLayerMap();
    },

    createMapView() {
      return new View({
        center: [0, 0],
        zoom: 2,
        minZoom: 3,
        maxZoom: 25,
      });
    },

    createRoadMapLayer() {
      return new TileLayer({
        title: "roadMapLayer",
        visible: true,
        source: this.roadMapSource,
      });
    },

    createRoadMapSource() {
      return new XYZ({
        url: "https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
        crossOrigin: "Anonymous",
      });
    },

    createOpenStreetMapSource() {
      return new OSM();
    },

    createAerialMapLayer() {
      return new TileLayer({
        title: "aerialMapLayer",
        visible: false,
        source: new BingMaps({
          key: "AtUEuM0GKaXtnijUSNcuIcmQItWcpMstzANuR3ONhBKeY3LsFPqLFznuwXMHP2kb",
          imagerySet: "Aerial",
          culture: "en-EN",
          labelOverlay: "visible",
          maxZoom: 19,
        }),
      });
    },

    /**
     * Create objects image layer
     * @returns {ImageLayer}
     */
    createObjectsImageLayer() {
      this.objectsImageSource = new ImageWMS({
        url: endpoints.FIBER_API_URL,
        params: {
          LAYERS: this.getActiveLayers(),
          VERSION: "1.1.1",
          SRS: "EPSG:3857",
        },
        serverType: "geoserver",
        crossOrigin: "anonymous",
        ratio: 1,
        imageLoadFunction: (image, src) =>
          this.loadGeoserverObjectsImage(image, src),
      });

      return new ImageLayer({
        tileOptions: { crossOrigin: "anonymous" },
        source: this.objectsImageSource,
      });
    },

    /**
     * Get active layers in the layers tab
     * @returns {string}
     */
    getActiveLayers() {
      this.checkedLayers = this.$store.getters.getNetworkActiveLayers;

      if (!this.checkedLayers) {
        this.checkedLayers = {};
      }

      let activeLayersString = "";

      if (this.checkedLayers.length > 0) {
        let activeLayers = [];
        for (const layerLevel of Object.values(this.checkedLayers)) {
          if (layerLevel.hasOwnProperty("layers")) {
            for (const filter of Object.values(layerLevel.layers)) {
              if (filter.layer && filter.layer.layer) {
                let layerName = filter.layer.layer;
                let geoserverLabel =
                  process.env.GEOSERVER_NETWORK + ":" + layerName;
                if (
                  !activeLayers.find(
                    (item) => item.geoserver_label === geoserverLabel
                  )
                ) {
                  activeLayers.push({
                    weight: layerLevel.weight,
                    geoserver_label: geoserverLabel,
                  });
                }
              }
            }
          }
        }
        activeLayersString = this.getActiveLayersAsString(activeLayers);
      }
      return activeLayersString;
    },

    /**
     * Converter active layers to String
     * @param activeLayers
     * @returns {*|string}
     */
    getActiveLayersAsString(activeLayers) {
      let activeLayersString = "";

      let orderedLayers = activeLayers.sort((a, b) =>
        a.weight > b.weight ? 1 : b.weight > a.weight ? -1 : 0
      );
      orderedLayers = orderedLayers.map((layer) => layer.geoserver_label);
      activeLayersString = orderedLayers.join(",");
      return activeLayersString;
    },

    /**
     * load image of Geoserver objects from active layers
     * @param image
     * @param src
     */
    loadGeoserverObjectsImage(image, src) {
      let img = image.getImage();
      let defaultImgSrc =
        "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

      if (this.checkedLayers.length > 0) {
        this.loading = true;
        this.fetchImageFromGeoServer({
          params: this.getParams(src),
          workspaceUUID: null,
          isNetwork: true,
        })
          .then((resp) => {
            src = this.convertImageFromGeoserver(resp);
            if (src) {
              img.src = src;
            } else {
              img.src = defaultImgSrc;
            }
          })
          .catch(() => {
            img.src = defaultImgSrc;
          })
          .then(() => {
            this.loading = false;
          });
      } else {
        img.src = defaultImgSrc;
      }
    },

    /**
     * Convert image from geoserver
     * @param resp
     * @returns {string}
     */
    convertImageFromGeoserver(resp) {
      let type = resp.headers["content-type"];

      if (type.indexOf("image") === 0) {
        let uInt8Array = new Uint8Array(resp.data);
        let charArray = [];
        for (let charCode of uInt8Array) {
          charArray.push(String.fromCharCode(charCode));
        }
        let imageData = charArray.join("");
        imageData = window.btoa(imageData);

        return "data:" + type + ";base64," + imageData;
      }
    },

    /**
     * Get Params to send Geoserver
     * @param src
     * @returns {string}
     */
    getParams(src) {
      let urlArray = src.split("?");
      let params = urlArray[1];
      params = params + "&scale=" + this.getScale();
      params = params + "&force_workspace_filter=" + false;
      return params;
    },

    /**
     * Returns the actual scale of the map
     * @returns {number}
     */
    getScale() {
      let resolution = this.mapView.getResolution();
      let projection = this.mapView.getProjection();
      let dpi = 25.4 / 0.28;
      let units = projection.getUnits();
      let mpu = METERS_PER_UNIT[units];
      return resolution * mpu * 39.37 * dpi;
    },

    /**
     * Create highlight layer
     * @returns {VectorLayer}
     */
    createHighlightLayer() {
      this.highlightLayerSource = new VectorSource({ wrapX: false });
      return new VectorLayer({
        source: this.highlightLayerSource,
      });
    },

    createHighlightBuildingsLayer() {
      this.highlightBuildingsLayerSource = new VectorSource({ wrapX: false });
      return new VectorLayer({
        source: this.highlightBuildingsLayerSource,
      });
    },

    /**
     * Highlight object
     * @param geoJson
     */
    highlightObjectOnHover(geoJson) {
      let features = new GeoJSON().readFeatures(geoJson, {
        featureProjection: "EPSG:3857",
      });
      let feature = features[0];
      let geometryType = feature.getGeometry().getType();
      let highlightStyle = {
        point: {
          radius: 15,
          strokeWidth: 2,
          fillColor: [255, 255, 255],
        },
        polygon: {
          strokeWidth: 2,
          fillColor: "rgba(250, 250, 250, 0.3)",
        },
        line: {
          strokeWidth: 10,
          strokeColor: [255, 255, 255],
          strokeWidthSecond: 8,
        },
      };

      feature.setStyle(
        this.getHighlightFeatureStyle(geometryType, highlightStyle)
      );
      this.highlightLayerSource.addFeature(feature);
    },
    updateSelectedBuilding(){
      const buildings = this.getBuildings;
      this.highlightBuildingsLayerSource.clear();
      buildings.forEach(building => {
        this.highlightBuilding(building.geojson);
      })
    },
    highlightBuilding(geoJson) {
      let features = new GeoJSON().readFeatures(geoJson, {
        featureProjection: "EPSG:3857",
      });
      let feature = features[0];
      let geometryType = feature.getGeometry().getType();
      let highlightStyle = {
        point: {
          radius: 15,
          strokeWidth: 2,
          fillColor: [255, 255, 255],
        },
        polygon: {
          strokeWidth: 2,
          fillColor: "rgba(250, 250, 250, 0.3)",
        },
        line: {
          strokeWidth: 10,
          strokeColor: [255, 255, 255],
          strokeWidthSecond: 8,
        },
      };

      feature.setStyle(
        this.getHighlightFeatureStyle(geometryType, highlightStyle)
      );
      this.highlightBuildingsLayerSource.addFeature(feature);
    },

    /**
     * Remove highlight from object
     */
    removeHighlightFromObjectOnLeave() {
      this.highlightLayerSource.clear();
    },

    /**
     * Generate style for highlight objects
     * @param type
     * @param highlightStyle
     * @returns {Style[]|Style}
     */
    getHighlightFeatureStyle(type, highlightStyle) {
      switch (type) {
        case "Spf":
          return new Style({
            image: new CircleStyle({
              radius: highlightStyle.point.radius,
              stroke: new Stroke({
                color: highlightStyle.point.strokeColor
                  ? highlightStyle.point.strokeColor
                  : [0, 0, 0],
                width: highlightStyle.point.strokeWidth,
                zIndex: 1,
                lineCap: 'round'
              }),
            }),
          });
        case "Point":
          return new Style({
            image: new CircleStyle({
              radius: highlightStyle.point.radius,
              stroke: new Stroke({
                color: highlightStyle.point.strokeColor
                  ? highlightStyle.point.strokeColor
                  : [0, 0, 0],
                width: highlightStyle.point.strokeWidth,
                zIndex: 1,
              }),
              fill: new Fill({
                color: highlightStyle.point.fillColor,
                zIndex: 1,
              }),
            }),
          });
        case "Polygon":
        case "MultiPolygon":
          return new Style({
            stroke: new Stroke({
              color: [0, 0, 0],
              width: highlightStyle.polygon.strokeWidth,
              zIndex: 1,
            }),
            fill: new Fill({
              color: highlightStyle.polygon.fillColor,
              zIndex: 1,
            }),
          });
        case "LineString":
        case "MultiLineString":
          return [
            new Style({
              stroke: new Stroke({
                color: [0, 0, 0],
                width: highlightStyle.line.strokeWidth,
                zIndex: 1,
              }),
            }),
            new Style({
              stroke: new Stroke({
                color: highlightStyle.line.strokeColor,
                width: highlightStyle.line.strokeWidthSecond,
                zIndex: 1,
              }),
            }),
          ];
      }
    },

    /**
     * Update map - layers
     */
    updateGeoserverLayers() {
      this.objectsImageSource.updateParams({
        LAYERS: this.getActiveLayers(),
        time: Date.now(),
      });
    },

    getObjectsImageSourceURL(coordinates) {
      let layers = this.objectsImageSource.params_.LAYERS;
      let resolution = this.mapView.getResolution();
      let projection = this.mapView.getProjection();

      return this.objectsImageSource.getGetFeatureInfoUrl(
        coordinates,
        resolution,
        projection,
        {
          QUERY_LAYERS: layers,
          LAYERS: layers,
          INFO_FORMAT: "application/json",
          FEATURE_COUNT: "100",
          scale: this.getScale(),
          force_workspace_filter: false,
        }
      );
    },

    /**
     * Active layer map selected
     */
    getActiveLayerMap() {
      let selectedLayer = JSON.parse(
        sessionStorage.getItem("active_network_layer_map")
      );
      let layers = this.map.getLayers();
      let source = layers.array_[0].values_.source;

      if (selectedLayer) {
        switch (selectedLayer.label) {
          case "Default Satellite":
            this.changeViewType("aerialMapLayer");
            break;
          case "Open Street Map":
            this.changeViewType("openStreetMap");
            break;
          case "Dark Map":
            /** Emits an event to set the map to dark mode **/
            this.$store.dispatch("SET_MAP_STYLE", "dark-map");
            this.changeViewType("roadMapLayer");
            source.setUrl(
              "https://{a-c}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"
            );
            this.isDarkMap === true;
            break;
          case "Light Map":
            /** Emits an event to set the map to light mode **/
            this.$store.dispatch("SET_MAP_STYLE", "light-map");
            this.changeViewType("roadMapLayer");
            source.setUrl(
              "https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
            );
            this.isDarkMap === false;
            break;
          default:
            this.changeViewType("newLayer");
            break;
        }
      }
    },

    /** Changes view type from selected layer map **/
    changeViewType(type) {
      let selectedLayer = JSON.parse(
        sessionStorage.getItem("active_network_layer_map")
      );
      this.roadMapLayer.setVisible(false);
      this.aerialMapLayer.setVisible(false);
      //get all layers
      const layersArray = this.map.getLayers().getArray();
      // go through all layer and delete previous layer added
      layersArray.forEach((element) => {
        if (element.type === "TILE") {
          if (element.getProperties().title === "newLayer") {
            this.map.removeLayer(element);
          }
        }
      });
      if (type !== "newLayer") {
        if (type === "openStreetMap") {
          this.roadMapLayer.setSource(this.openStreetMapSource);
          this.roadMapLayer.setVisible(true);
        } else {
          if (type === "roadMapLayer") {
            this[type].setSource(this.roadMapSource);
          }
          this[type].setVisible(true);
        }
      } else {
        //create new layer
        const layer = new TileLayer({
          title: "newLayer",
          visible: true,
          source: new XYZ({
            url: selectedLayer.url,
          }),
        });
        //add layer to map
        this.map.getLayers().insertAt(0, layer);
      }
      this.map.updateSize();
    },

    /**
     *
     * @param style
     */
    changeLocalMapStyle(style) {
      let layers = this.map.getLayers();
      let source = layers.array_[0].values_.source;

      if (style === "dark-map") {
        source.setUrl(
          "https://{a-c}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"
        );
        this.isDarkMap = true;
      } else if (style === "light-map") {
        source.setUrl(
          "https://{a-c}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
        );
        this.isDarkMap = false;
      }
    },

    //////////////////////////////
    /*Object interaction methods*/
    //////////////////////////////
    /**
     * Get the objects near the selected point
     * @param e
     * @returns {Promise<unknown>}
     */
    getObjects(e) {
      return new Promise((resolve) => {
        let url = this.getObjectsImageSourceURL(e.coordinate);

        this.loading = true;
        this.fetchFeaturesFromGeoServer({ data: url, isNetwork: true })
          .then((data) => {
            this.contextMenuPixel = e.pixel;
            this.contextMenuCoords = e.coordinate;

            if (this.failureAnalysisToolIsActive) {
              this.createXMarker();
              this.prepareObjectList(
                this.getFeaturesAvailableForFailureAnalysis(data.features)
              );
            } else {
              let features = data.features;
              let numberOfFeatures = data.numberReturned;
              if (this.singlePointofFailureIsActive) {
                const buildings = features.filter(
                  (item) => item.properties.object_type === "building"
                );
                features = [...buildings];
                numberOfFeatures = buildings.length;
              }
              if (numberOfFeatures > 1) {
                this.prepareObjectList(features);
              } else if (numberOfFeatures === 1) {
                this.selectSingleObject(features);
              }
            }
          })
          .catch((error) => console.error(error))
          .then(() => {
            this.loading = false;
            resolve();
          });
      });
    },

    /**
     * Select the object if the object number of the selected point is one
     * @param features
     */
    selectSingleObject(features) {
      let feature = features[0];
      let objectIdentifiers = this.getObjectIdentifiers(feature);
      if (objectIdentifiers) {
        this.selectObject(objectIdentifiers);
      }
    },

    /**
     * Prepare the list of objects if the number of objects of the selected point is more than 1
     * @param features
     */
    prepareObjectList(features) {
      this.objectList.list = [];
      features.forEach((feature) => {
        let featureIdentifiers = this.getObjectIdentifiers(feature);
        if (featureIdentifiers && featureIdentifiers.hasPermissionShow) {
          this.objectList.list.push(featureIdentifiers);
        }
      });
      if (!this.isObjectListEmpty()) {
        this.objectList.open = true;
      }
    },

    /**
     * Define if the object list is empty. There are no objects at the selected point
     * @returns {boolean}
     */
    isObjectListEmpty() {
      return this.objectList.list.length === 0;
    },

    /**
     * Returns data for object identifiers
     * @param feature
     * @returns {boolean|{marked: boolean, geojson: (string|*), hasPermissionShow: Boolean, endpoint: *, coordinates, id: string, layerName: *, label, type: *, uuid, objectType}}
     */
    getObjectIdentifiers(feature) {
      let layer = this.getObjectLayer(feature.id);

      if (layer === "fc_phase" || layer === "fc_field_influence_line") {
        return false;
      }

      let initials = this.$store.getters.getLayerInitials(layer);
      let endpoint = this.$store.getters.getLayerEndpoint(layer);
      let typeName = this.$store.getters.getLayerTypeName(layer);
      let permissionName = this.$store.getters.getLayerPermissionName(layer);

      return {
        type: typeName,
        id: this.getObjectIDNumber(feature.id),
        uuid: feature.properties[initials + "_uuid"],
        endpoint: endpoint,
        layerName: layer,
        coordinates: feature.geometry.coordinates,
        objectType: feature.properties.object_type,
        marked: false,
        geojson: feature.properties.geo_json,
        label: feature.properties[initials + "_label"],
        hasPermissionShow: this.userHasFibercloudPermission(
          permissionName,
          "show"
        ),
      };
    },

    /**
     * Returns the object's layer
     * @param entireID (example: fc_fiber_distribution_point.66167)
     * @returns {*}
     */
    getObjectLayer(entireID) {
      return entireID.split(".")[0];
    },

    /**
     * Returns the object's id
     * @param entireID (example: fc_fiber_distribution_point.66167)
     * @returns {string}
     */
    getObjectIDNumber(entireID) {
      return entireID.substr(entireID.indexOf(".") + 1);
    },

    /**
     * Get complete object data from the identifier object.
     * After obtaining the data, the form corresponding to the selected object opens
     * @param objectIdentifiers
     */
    getObjectDataFromList(objectIdentifiers) {
      this.closeObjectList();
      this.fetchObjectData(objectIdentifiers).then((objectData) => {
        this.$refs.objectForm.openNewForm(
          objectIdentifiers.layerName,
          objectData
        );
        this.closeObjectList();
      });
    },

    /**
     * Requests the BE to return the selected object
     * @param objectIdentifiers
     * @returns {Promise<*>}
     */
    async fetchObjectData(objectIdentifiers) {
      try {
        this.loading = true;
        let resp = await this.fetchObjectByUUID({
          endpoint: objectIdentifiers.endpoint,
          objectUUID: objectIdentifiers.uuid,
          fieldsToInclude: this.getFieldsToInclude(objectIdentifiers),
          fieldsToAppend: null,
          workspaceUuidInclude: false,
        });
        this.loading = false;
        return resp.data.data[0];
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },

    /**
     * Get fields to include for each layer
     * @returns {string}
     */
    getFieldsToInclude(objectIdentifiers) {
      switch (objectIdentifiers.endpoint) {
        case "closures":
          return "technicalpoint,fiberterminalbox,images";
        case "technicalpoints":
          return "images";
        case "buildings":
          return "images,technicalsite,technicallocals,customer_premises";
        case "sites":
        case "technicallocals":
          return "technicalsite,node";
        case "technicalsites":
          return "technicallocals,images";
        case "cables":
          return "conducts";
        default:
          return "";
      }
    },

    /**
     * Close the object list
     */
    closeObjectList() {
      this.objectList.open = false;
      this.objectList.list = [];
    },

    /**
     * Filters the features so that only the available layers are selected
     *
     * @param {Array} features - the array of features selected
     * @returns {Array} the array of available features
     */
    getFeaturesAvailableForFailureAnalysis(features) {
      let availableFeatures = [];
      features.forEach((feature) => {
        switch (this.getObjectLayer(feature.id)) {
          case "fc_closure":
          case "fc_pathway":
          case "fc_cable":
          case "fc_optical_line_termination":
          case "fc_fiber_distribution_point":
          case "fc_technical_local_olt":
          case "fc_technical_local_fdp":
            availableFeatures.push(feature);
            break;
          default:
            break;
        }
      });
      return availableFeatures;
    },

    /**
     * If there is a selected tool and it's the Failure Analysis tool, the analysis is executed
     * Else, the data from the selected object is loaded
     *
     * @param {Object} objectIdentifiers - the properties of the object
     */
    selectObject(objectIdentifiers) {
      if (this.failureAnalysisToolIsActive) {
        this.failureAnalysisSource.clear();
        this.analyseObjectFailureImpact(objectIdentifiers);
      } else if (this.singlePointofFailureIsActive) {
        if (objectIdentifiers.objectType === "building") {
          if(this.spfSelectionType){
            this.singlePointofFailureSource.clear();
            this.highlightBuilding(objectIdentifiers.geojson);
            this.addBuildingsSPF(objectIdentifiers);
            this.closeObjectList();
          }else{
            this.$toasted.global.toast_error("Select a selection type");
          }
        } else {
          this.$toasted.global.toast_error("Select a building please");
        }
      } else {
        this.getObjectDataFromList(objectIdentifiers);
      }
    },

    /**
     * Closes the object list
     * Executes the failure analysis and depending on the result gives feedback to the user
     * If there are no objects affected, a toast message indicating it is shown
     * Else, the affected objects are highlighted
     *
     * @param {Object} objectIdentifiers - the properties of the object
     */
    async analyseObjectFailureImpact(objectIdentifiers) {
      this.closeObjectList();
      try {
        this.$emit("loading", true);
        let affectedObjects = await this.executeFailureAnalysis({
          object_type: this.getObjectType(objectIdentifiers.layerName),
          object_uuid: objectIdentifiers.uuid,
          coordinates: {
            projection: 3857,
            x: this.contextMenuCoords[0],
            y: this.contextMenuCoords[1],
          },
        });

        if (affectedObjects.features.length < 1) {
          this.$toasted.global.toast_success(
            "The failure of this object does not affect the network."
          );
        } else {
          this.failureAnalysisSource.addFeature(this.xMarkerFeature);
          affectedObjects.features.forEach((featureObject) => {
            this.addFeatureToFailureAnalysisLayer(featureObject);
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.$emit("loading", false);
      }
    },

    async runSinglePointFailure() {
      try {
        this.$emit("loading", true);
        const buildingsList = this.getBuildings;
        const buildings = buildingsList.map((building) => building.uuid)
        let affectedObjects = await this.executeSinglePointFailure({
          buildings,
          field: "uuid"
        });
        if (affectedObjects.path.features.length > 0) {
          affectedObjects.path.features.forEach((featureObject) => {
            this.addFeatureToSinglePointFailureLayer(featureObject);
          });
        }
        if (affectedObjects.message) {
          this.$toasted.global.toast_error(affectedObjects.message);
        }
      } catch (e) {
        this.clearAll();
        if(e.response.data.hasOwnProperty('data')){
          this.$toasted.global.toast_error(e.response.data.data.message);
        }else if(e.response.data.hasOwnProperty('message')){
          this.$toasted.global.toast_error(e.response.data.message);
        }
      } finally {
        this.$emit("loading", false);
      }
    },

    /**
     * Verifies the layer of the object and returns the object type
     *
     * @param {String} layerName - the layer of the object
     * @returns {String} the object type
     */
    getObjectType(layerName) {
      let objectType;
      switch (layerName) {
        case "fc_technical_local_olt":
        case "fc_technical_local_fdp":
          objectType = "fc_technical_local";
          break;
        case "fc_optical_line_termination":
        case "fc_fiber_distribution_point":
          objectType = "fc_technical_site";
          break;
        default:
          objectType = layerName;
          break;
      }
      return objectType;
    },

    /**
     * Creates the layer for the failure analysis objects
     *
     * @returns {VectorLayer} the failure analysis layer
     */
    createFailureAnalysisLayer() {
      this.failureAnalysisSource = new VectorSource({ wrapX: false });
      return new VectorLayer({
        source: this.failureAnalysisSource,
      });
    },

    /**
     * Creates the layer for the single point failure objects
     *
     * @returns {VectorLayer} the failure layer
     */
    createSinglePointofFailureLayer() {
      this.singlePointofFailureSource = new VectorSource({ wrapX: false });
      return new VectorLayer({
        source: this.singlePointofFailureSource,
      });
    },

    /**
     * Adds a feature to the failure analysis layer
     *
     * @param {Object} featureObject - the feature to be added to the layer
     */
    addFeatureToFailureAnalysisLayer(featureObject) {
      let feature = new GeoJSON().readFeature(featureObject.geometry, {
        featureProjection: "EPSG:3857",
      });
      let geometryType = featureObject.geometry.type;
      let highlightStyle = {
        point: {
          radius: 15,
          strokeWidth: 2,
          strokeColor: [255, 138, 128],
          fillColor: [211, 47, 47, 0.5],
        },
        polygon: {
          strokeWidth: 2,
          fillColor: "rgba(211, 47, 47, 0.5)",
        },
        line: {
          strokeWidth: 14,
          strokeColor: [255, 138, 128],
          strokeColorSecond: [211, 47, 47, 0.5],
          strokeWidthSecond: 10,
        },
      };

      feature.setStyle(
        this.getHighlightFeatureStyle(geometryType, highlightStyle)
      );

      this.failureAnalysisSource.addFeature(feature);
    },

    addFeatureToSinglePointFailureLayer(featureObject) {
      let feature = new GeoJSON().readFeature(featureObject.geometry, {
        featureProjection: "EPSG:3857",
      });
      let highlightStyle = {
        point: {
          radius: 20,
          strokeWidth: 4,
          strokeColor: [255, 138, 128],
          fillColor: [211, 47, 47, 0.5],
        },
        polygon: {
          strokeWidth: 2,
          fillColor: "rgba(211, 47, 47, 0.5)",
        },
        line: {
          strokeWidth: 14,
          strokeColor: [255, 138, 128],
          strokeColorSecond: [211, 47, 47, 0.5],
          strokeWidthSecond: 10,
        },
      };

      feature.setStyle(
        this.getHighlightFeatureStyle('Spf', highlightStyle)
      );

      this.singlePointofFailureSource.addFeature(feature);
    },

    /**
     * Creates the marker feature
     */
    createXMarker() {
      this.xMarkerFeature = new Feature({
        geometry: new Point(this.contextMenuCoords),
      });

      let xStyle = new Style({
        image: new RegularShape({
          fill: new Fill({ color: "red" }),
          stroke: new Stroke({ color: "black", width: 4 }),
          points: 4,
          radius: 20,
          radius2: 0,
          angle: Math.PI / 4,
        }),
      });

      this.xMarkerFeature.setStyle(xStyle);
    },

    createMeasurementsLayer() {
      this.measurementsLayerSource = new VectorSource();
      return new VectorLayer({
        source: this.measurementsLayerSource,
        style: new Style({
          fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
          }),
          stroke: new Stroke({
            color: "#ffcc33",
            width: 2,
          }),
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({
              color: "#ffcc33",
            }),
          }),
        }),
      });
    },

    addMeasurementInteraction() {
      this.disableMapClickEvents();
      unByKey(this.singleClickEvent);
      this.measurementsDraw = this.updateMeasurementDrawLayer();
      this.map.addInteraction(this.measurementsDraw);
      this.createMeasureTooltip();
      let listener;
      this.measurementsDraw.on("drawstart", (evt) =>
        this.onMeasurementDrawStart(listener, evt)
      );
      this.measurementsDraw.on("drawend", () =>
        this.onMeasurementDrawEnd(listener)
      );
    },

    updateMeasurementDrawLayer() {
      let type = "";
      switch (this.selectedTool.id) {
        case "length":
          type = "LineString";
        case "angle":
          type = "LineString";
          break;
        case "area":
          type = "Polygon";
          break;
      }
      return new Draw({
        source: this.measurementsLayerSource,
        type: type,
        style: new Style({
          fill: new Fill({
            color: "rgba(255, 255, 255, 0.2)",
          }),
          stroke: new Stroke({
            color: "rgba(0, 0, 0, 0.5)",
            lineDash: [10, 10],
            width: 2,
          }),
          image: new CircleStyle({
            radius: 5,
            stroke: new Stroke({
              color: "rgba(0, 0, 0, 0.7)",
            }),
            fill: new Fill({
              color: "rgba(255, 255, 255, 0.2)",
            }),
          }),
        }),
      });
    },

    onMeasurementDrawStart(listener, evt) {
      this.sketch = evt.feature;
      let tooltipCoordinate = evt.coordinate;
      listener = this.sketch.getGeometry().on("change", (evt) => {
        let geom = evt.target;
        this.updateMeasurementTooltip(geom, tooltipCoordinate);
        this.manageAngleMeasurementCreation(geom);
      });
    },

    updateMeasurementTooltip(geom, tooltipCoordinate) {
      let output;
      if (geom instanceof Polygon) {
        output = this.formatMeasuredAreaOutput(geom);
        tooltipCoordinate = geom.getInteriorPoint().getCoordinates();
      } else if (geom instanceof LineString) {
        if (this.selectedTool.id === "length") {
          output = this.formatMeasuredLengthOutput(geom);
          tooltipCoordinate = geom.getLastCoordinate();
        } else if (this.selectedTool.id === "angle") {
          output = this.formatMeasuredAngleOutput(geom);
          tooltipCoordinate = geom.getLastCoordinate();
        }
      }
      this.measureTooltipElement.innerHTML = output;
      this.measureTooltipElement.id = "tooltip-" + geom.ol_uid;
      this.measureTooltip.setPosition(tooltipCoordinate);
    },

    /**
     * Format length output.
     * @param {LineString} line The line.
     * @return {string} The formatted length.
     */
    formatMeasuredLengthOutput(line) {
      let output;
      let length = getLength(line);
      if (length > 100) {
        output = Math.round((length / 1000) * 100) / 100 + " km";
      } else {
        output = Math.round(length * 100) / 100 + " m";
      }
      return output;
    },

    manageAngleMeasurementCreation(geom) {
      if (this.selectedTool === "angle") {
        let hasOneSegment = geom.getCoordinates().length > 2;
        if (hasOneSegment) {
          this.measurementsDraw.finishDrawing();
        }
      }
    },

    /**
     * Format angle output.
     * @param {LineString} line The line.
     * @return {string} The formatted angle.
     */
    formatMeasuredAngleOutput(line) {
      let startCoordinate = line.getCoordinateAt(0);
      startCoordinate = toLonLat(startCoordinate);
      let endCoordinate = line.getCoordinateAt(1);
      endCoordinate = toLonLat(endCoordinate);
      let turf = require("@turf/turf");
      let bearing = turf.bearing(startCoordinate, endCoordinate);
      let output = bearingToAzimuth(bearing).toFixed(2);
      return output + " º";
    },

    /**
     * Format area output.
     * @param {Polygon} polygon The polygon.
     * @return {string} Formatted area.
     */
    formatMeasuredAreaOutput(polygon) {
      let output;
      let area = getArea(polygon);
      if (area > 10000) {
        output = Math.round((area / 1000000) * 100) / 100 + " km<sup>2</sup>";
      } else {
        output = Math.round(area * 100) / 100 + " m<sup>2</sup>";
      }
      return output;
    },

    onMeasurementDrawEnd(listener) {
      this.measureTooltipElement.className = "ol-tooltip ol-tooltip-static";
      this.measureTooltip.setOffset([0, -7]);
      this.sketch = null;
      // unset tooltip so that a new one can be created
      this.measureTooltipElement = null;
      this.createMeasureTooltip();
      unByKey(listener);
    },

    /**
     * Creates a new measure tooltip
     */
    createMeasureTooltip() {
      if (this.measureTooltipElement) {
        this.measureTooltipElement.parentNode.removeChild(
          this.measureTooltipElement
        );
      }
      this.measureTooltipElement = document.createElement("div");
      this.measureTooltipElement.className = "ol-tooltip ol-tooltip-measure";
      this.measureTooltip = new Overlay({
        element: this.measureTooltipElement,
        offset: [0, -15],
        positioning: "bottom-center",
      });
      this.map.addOverlay(this.measureTooltip);
    },

    drawNewPathway(e) {
      this.lineStringWKT = [];
      this.pathwayVerticesCoordinates = [];
      this.draw = this.drawNewLineString();
      this.createPathwayTooltip();
      this.draw.on("drawstart", () => this.addVertexToNewPathway(e));
      this.draw.startDrawing_(e);

      let listener = this.draw.sketchFeature_.values_.geometry.on(
        "change",
        (e) => {
          let coords = e.target.getCoordinates();
          if (coords.length > 1) {
            this.updatePathwayTooltip(coords[coords.length - 1]);
          }
        }
      );

      this.clickEvent = this.map.on("click", (click) =>
        this.addVertexToNewPathway(click)
      );

      this.rightClickEvent = this.map.on("contextmenu", () => {
        this.draw.removeLastPoint();
        this.pathwayVerticesCoordinates.pop();
        if (this.pathwayVerticesCoordinates.length === 0) {
          this.createPathwayTooltip();
        }
      });

      this.draw.on("drawend", () => {
        this.map.removeOverlay(this.pathwayTooltip);
        this.finishPathwayDrawing();
      });
    },

    updatePathwayTooltip(tooltipCoordinate) {
      if (
        this.draw &&
        this.draw.sketchFeature_ &&
        this.draw.sketchFeature_.values_
      ) {
        let feature = this.draw.sketchFeature_.values_.geometry;
        let length = this.formatMeasuredLengthOutput(feature);
        this.pathwayTooltipElement.innerHTML = length;
        this.pathwayTooltip.setPosition(tooltipCoordinate);
      }
    },

    /**
     * Creates a new Draw, a tooltip indicating the length of the line and
     * listens to events to add vertices and finish the draw.
     * After the draw ends it draws the points in the map
     */
    drawNewBulkTPLine(e) {
      this.lineStringWKT = [];
      this.techPointsCoordinates = [];
      this.bulkTPLineVerticesCoordinates = [];
      this.draw = this.drawNewLineString();
      this.createPathwayTooltip();
      this.draw.on("drawstart", () => this.addVertexToBulkLine(e));
      this.draw.startDrawing_(e);

      let listener = this.draw.sketchFeature_.values_.geometry.on(
        "change",
        (e) => {
          let coords = e.target.getCoordinates();
          if (coords.length > 1) {
            this.updatePathwayTooltip(coords[coords.length - 1]);
          }
        }
      );

      this.clickEvent = this.map.on("click", (click) =>
        this.addVertexToBulkLine(click)
      );

      this.rightClickEvent = this.map.on("contextmenu", () => {
        this.draw.removeLastPoint();
        this.bulkTPLineVerticesCoordinates.pop();
        if (this.bulkTPLineVerticesCoordinates.length === 0) {
          this.createPathwayTooltip();
        }
      });

      this.draw.on("drawend", () => {
        this.disableMapClickEvents();
        this.map.removeOverlay(this.pathwayTooltip);
        this.previewBulkTP();
      });
    },

    drawNewLineString() {
      this.disableMapClickEvents();
      unByKey(this.singleClickEvent);

      this.newObjectDraw = new Draw({
        features: new Collection([]),
        type: "LineString",
        condition: (e) => {
          return e.pointerEvent.buttons === 1; // only allows drawing on leftclick (1),
        },
      });

      this.map.addInteraction(this.newObjectDraw);

      return this.newObjectDraw;
    },

    startArea(e) {
      this.disableMapClickEvents();
      this.resetArea(false);

      this.drawNewPolygon(e, (promise) => {
        promise.then((result) => {
          this.createArea({
            text_geom: result,
            projection: 3857,
          });
        });
      });
    },

    drawNewPolygon(e, promise) {
      this.newObjectDraw = new Draw({
        features: new Collection([]),
        ...(this.currentActionType === "select" && {
          source: this.drawLayerSource,
        }),
        freehand: false,
        type: "Polygon",
      });
      let draw = this.newObjectDraw;

      this.map.addInteraction(draw);
      draw.startDrawing_(e);

      this.singleClickEvent = this.map.on("singleclick", (ev) => {
        if (!this.isClickInsideWorkspace(ev)) {
          draw.removeLastPoint();
        }
      });

      this.rightClickEvent = this.map.on("contextmenu", () =>
        draw.removeLastPoint()
      );

      promise(
        new Promise((resolve) => {
          draw.on("drawend", (e) => {
            let polygonWKT = this.getFeatureWKT(e.feature);
            this.map.removeInteraction(draw);
            resolve(polygonWKT);
          });
        })
      );
    },

    disableMapClickEvents() {
      unByKey(this.clickEvent);
      unByKey(this.rightClickEvent);
      unByKey(this.doubleClickEvent);
    },

    resetMapClickEvents() {
      this.disableMapClickEvents();
      this.enableMapClickEvents();
    },

    enableMapClickEvents() {
      this.mapClickListener();
      this.doubleClickListener();
      this.rightClickListener();
    },

    /**
     * Adds the vertex to the line
     */
    addVertexToBulkLine(e) {
      if (this.isClickInsideWorkspace(e)) {
        this.bulkTPLineVerticesCoordinates.push(e.coordinate);
      } else {
        this.newObjectDraw.removeLastPoint();
        this.loading = false;
      }
    },

    /**
     * Format area output.
     * @param {Polygon} polygon The polygon.
     * @return {string} Formatted area.
     */
    formatMeasuredAreaOutput(polygon) {
      let output;
      let area = getArea(polygon);
      if (area > 10000) {
        output = Math.round((area / 1000000) * 100) / 100 + " km<sup>2</sup>";
      } else {
        output = Math.round(area * 100) / 100 + " m<sup>2</sup>";
      }
      return output;
    },

    getPointCoordsFromWKT(wkt) {
      return new WKT().readGeometry(wkt).getCoordinates();
    },

    setLineStringMarkPoints(geom) {
      this.setMarkPointPosition(geom.getFirstCoordinate());
      this.setMarkPointPosition(geom.getLastCoordinate());
    },

    getMultiLineEndCoordinates(geom) {
      let lineStrings = geom.getLineStrings();
      let lineStringCoordinates = [];
      for (let lineString of lineStrings) {
        lineStringCoordinates.push(lineString.getFirstCoordinate());
        lineStringCoordinates.push(lineString.getLastCoordinate());
      }
    },

    createPathwayTooltip() {
      if (this.pathwayTooltipElement) {
        this.pathwayTooltipElement.parentNode.removeChild(
          this.pathwayTooltipElement
        );
      }
      this.pathwayTooltipElement = document.createElement("div");
      this.pathwayTooltipElement.className = "ol-tooltip ol-tooltip-measure";
      this.pathwayTooltip = new Overlay({
        element: this.pathwayTooltipElement,
        offset: [0, -15],
        positioning: "bottom-center",
      });
      this.map.addOverlay(this.pathwayTooltip);
    },

    highlightObjectJoinCablesMenu(geoJson) {
      let features = new GeoJSON().readFeatures(geoJson, {
        featureProjection: "EPSG:3857",
      });
      let feature = features[0];
      let highlightStyle = this.getHighlightStyle();
      let geometryType = feature.getGeometry().getType();

      feature.setStyle(
        this.getHighlightFeatureStyle(geometryType, highlightStyle)
      );
      this.joinCableMenu.highlightObjectLayerSource.addFeature(feature);
    },

    highlightObjectFiberCircuitSelected(geoJson) {
      let features = new GeoJSON().readFeatures(geoJson, {
        featureProjection: "EPSG:3857",
      });
      let feature = features[0];
      let highlightStyle = this.getHighlightStyle();
      let geometryType = feature.getGeometry().getType();

      feature.setStyle(
        this.getHighlightFeatureStyle(geometryType, highlightStyle)
      );
      this.fiberCircuit.highlightObjectLayerSource.addFeature(feature);
    },

    highlightObjectSelected(data) {
      if (data && data.geoJson) {
        let features = new GeoJSON().readFeatures(data.geoJson, {
          featureProjection: "EPSG:3857",
        });
        let feature = features[0];
        feature.setId(data.uuid);
        let geometryType = feature.getGeometry().getType();
        let highlightStyle = {
          point: {
            radius: 10,
            strokeWidth: 1,
            fillColor: "#4fa2b7",
          },
          polygon: {
            strokeWidth: 1,
            fillColor: "#4fa2b7",
          },
          line: {
            strokeWidth: 6,
            strokeColor: "#4fa2b7",
            strokeWidthSecond: 5,
          },
        };
        feature.setStyle(
          this.getHighlightFeatureStyle(geometryType, highlightStyle)
        );
        this.highlightObjectLayerSource.addFeature(feature);
      }
    },

    newObjectIsBeingDrawn() {
      return (
        this.newObjectDraw &&
        this.newObjectDraw.getActive() &&
        this.newObjectDraw.sketchFeature_ &&
        this.newObjectDraw.sketchFeature_.getGeometry()
      );
    },

    createModifyInteraction() {
      this.modifyInteraction = new Modify({ source: this.drawLayerSource });
      this.map.addInteraction(this.modifyInteraction);
      this.modifyInteraction.setActive(false);
    },

    addMarkPoint(feature) {
      let geom = feature.getGeometry();
      let geometryType = geom.getType();
      let coordinates = geom.flatCoordinates;
      switch (geometryType) {
        case "Point":
          this.setMarkPointPosition(coordinates);
          break;

        case "Polygon":
        case "MultiPolygon":
          this.setPolygonMarkPoint(coordinates);
          break;

        case "LineString":
          this.setLineStringMarkPoints(geom);
          break;

        case "MultiLineString":
          if (this.isTrulyMultiLineString(geom)) {
            coordinates = this.getMultiLineEndCoordinates(geom);
            if (coordinates.length === 2) {
              this.setMarkPointPosition(coordinates[0]);
              this.setMarkPointPosition(coordinates[1]);
            }
          } else {
            this.setLineStringMarkPoints(geom);
          }
          break;
      }
    },

    setPolygonMarkPoint(coordinates) {
      let polygonCoords = [];
      for (let i = 0; i < coordinates.length; i = i + 2) {
        polygonCoords.push([coordinates[i], coordinates[i + 1]]);
      }
      let turf = require("@turf/turf");
      let polygon = turf.polygon([polygonCoords]);
      let center = turf.centerOfMass(polygon);
      center = center.geometry.coordinates;
      this.setMarkPointPosition(center);
    },

    setMarkPointPosition(coordinates) {
      let x = coordinates[0];
      let y = coordinates[1];
      let feature = this.markPointFeature.clone();
      feature.getGeometry().setCoordinates([x, y]);
      this.highlightLayerSource.addFeature(feature);
    },

    /**
     * Deletes the measured area and the respective tooltip
     */
    deleteMeasurementArea() {
      let features = this.measurementsLayerSource.getFeatures();
      for (const feature of features) {
        if (
          !!this.measurementAreaToDelete.getGeometry() &&
          feature.getGeometry().ol_uid ===
            this.measurementAreaToDelete.getGeometry().ol_uid
        ) {
          document
            .getElementById("tooltip-" + feature.getGeometry().ol_uid)
            .remove();
        }
      }
      this.measurementsLayerSource.removeFeature(this.measurementAreaToDelete);
    },

    /**
     * Calculates the coordinates of the technical points.
     * First it draws the vertices of the bulk line, then it draws the rest of
     * the technical points
     */
    calculateTechPointsCoordinates() {
      let coords;
      for (let i = 0; i < this.bulkTPLineVerticesCoordinates.length; i++) {
        coords = this.bulkTPLineVerticesCoordinates[i];
        this.addTechPointPreviewFeature(
          0,
          coords,
          this.bulkTPLineVerticesCoordinates.length,
          false
        );
      }

      let totalLineStringLength = getLength(this.bulkFeature.getGeometry());
      let numberOfTPs = this.getTotalNumberOfTechPoints(totalLineStringLength);
      this.bulkFeature.getGeometry().forEachSegment((start, end) => {
        let segmentLineString = new LineString([start, end]);
        numberOfTPs = this.getNumberOfTechPointsInSegment(
          getLength(segmentLineString),
          totalLineStringLength
        );
        for (let i = 0; i < Math.floor(numberOfTPs); i++) {
          coords = segmentLineString.getCoordinateAt((i + 1) / numberOfTPs);
          this.addTechPointPreviewFeature(
            end,
            coords,
            this.getTotalNumberOfTechPoints(totalLineStringLength),
            true
          );
        }
      });

      this.bulk_tp.tooManyPointsDrawn =
        this.techPointsCoordinates.length -
          this.bulkTPLineVerticesCoordinates.length >
        100;
    },

    clearMeasurements() {
      this.measurementsLayerSource.clear();
      this.setSelectedMeasureTool(null);
      this.map
        .getOverlays()
        .getArray()
        .slice(0)
        .forEach((overlay) => {
          this.map.removeOverlay(overlay);
        });
    },

    getHighlightStyle() {
      return {
        point: {
          radius: 15,
          strokeWidth: 2,
          fillColor: [255, 255, 255],
        },
        polygon: {
          strokeWidth: 2,
          fillColor: "rgba(250, 250, 250, 0.3)",
        },
        line: {
          strokeWidth: 6,
          strokeColor: "#FFC000",
          strokeWidthSecond: 5,
        },
      };
    },

    /**
     * Changes the openLayers map, the google street view map and the pointer position
     * based on a position change on the panorama
     */
    onPanoramaPositionChange() {
      if (this.showStreetView) {
        let pos = this.panoramaMap.getPosition();
        this.googleMap.setCenter(pos);
        this.centerMap(pos.lng(), pos.lat());
        this.mapView.setZoom(this.googleMap.getZoom());

        this.streetViewPointer
          .getGeometry()
          .setCoordinates(
            transform([pos.lng(), pos.lat()], "EPSG:4326", "EPSG:3857")
          );
        this.updateStreetViewPointerRotation();
      }
    },
    updateStreetViewPointerRotation() {
      if (this.showStreetView) {
        let pointerStyle = this.streetViewPointer.getStyle();
        let icon = pointerStyle.getImage();
        let rotation =
          ((this.panoramaMap.getPov().heading - 90) * Math.PI) / 180;
        icon.setRotation(rotation);

        this.streetViewPointerLayer.getSource().dispatchEvent("change");
      }
    },
    clearAll() {
      this.deleteAllSPF();
      this.singlePointofFailureSource.clear();
      this.highlightBuildingsLayerSource.clear();
    },
    closeSinglePointWindow() {
      this.setSelectedTool(null);
      this.singlePointofFailureSource.clear();
      this.highlightBuildingsLayerSource.clear();
    },
    closeFailureAnalysisWindow() {
      this.setSelectedTool(null);
      this.failureAnalysisSource.clear();
    },
  },
};
</script>

<style scoped>
.main-content {
  background-color: var(--screen);
  position: absolute;
  left: 375px;
  height: 100%;
  width: calc(100% - 375px - 45px);
  z-index: 2;
  display: flex;
}
</style>



// WEBPACK FOOTER //
// src/components/Network/Map/NetworkMap.vue